// Google Tag Manager bridge for Google Ads conversion tracking.
//
// The GTM container script is injected only when a container id is configured
// (constants.ts GTM_CONTAINER_ID) and the app is not running on localhost.
// dataLayer pushes are always performed regardless: they accumulate in
// window.dataLayer, which keeps them inspectable during development and lets
// GTM replay queued events if the container loads later.
import { generalAppVariables } from "@/constants";

// Event names are a fixed contract with the GTM container triggers
// (see "Countly Flex — Google Ads + GTM Conversion Tracking" spec, section 3).
export const GtmEvent = {
  ACCOUNT_CREATED: "account_created",
  WORKSPACE_CREATED: "workspace_created",
  PLAN_SELECTED: "plan_selected",
  CONFIG_COMPLETED: "config_completed",
  INCLUDED_VIEWED: "included_viewed",
  CAPABILITIES_SELECTED: "capabilities_selected",
  EXTRAS_VIEWED: "extras_viewed",
  CHECKOUT_STARTED: "checkout_started",
  SUBSCRIPTION_COMPLETE: "subscription_complete",
} as const;

export type GtmEventName = (typeof GtmEvent)[keyof typeof GtmEvent];

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export function pushGtmEvent(event: GtmEventName, payload: Record<string, unknown> = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });

  if (window.location.hostname === "localhost" || localStorage.getItem("gtm_debug") === "1") {
    console.log(`[GTM] ${event}`, payload);
  }
}

export function initGtm() {
  const containerId = generalAppVariables.GTM_CONTAINER_ID;
  // localhost is excluded so dev traffic never pollutes audiences/conversions;
  // set localStorage gtm_debug=1 to opt in locally (e.g. for GTM Preview).
  const isDebugEnabled = localStorage.getItem("gtm_debug") === "1";
  if (!containerId || (window.location.hostname === "localhost" && !isDebugEnabled)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}
